import React, { useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { InstanceComment } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

const INSTANCE_COMMENTS_QUERY_KEY = 'ACTION_LIBRARY_INSTANCE_COMMENTS';

type InstanceCommentsDialogProps = {
  open: boolean;
  onClose: () => void;
  instanceId: number;
};

const InstanceCommentsDialog: React.FC<InstanceCommentsDialogProps> = ({ open, onClose, instanceId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newCommentText, setNewCommentText] = useState('');

  const { data: commentsData, isLoading } = useQuery(
    [INSTANCE_COMMENTS_QUERY_KEY, instanceId],
    () => actionLibraryInstancesApi.getInstanceComments(instanceId).then(res => res.data),
    { enabled: open && !!instanceId },
  );

  const createCommentMutation = useMutation((text: string) => actionLibraryInstancesApi.createInstanceComment(instanceId, { text }), {
    onSuccess: () => {
      queryClient.invalidateQueries([INSTANCE_COMMENTS_QUERY_KEY, instanceId]);
      setNewCommentText('');
    },
  });

  const comments = commentsData?.results ?? [];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newCommentText.trim();
    if (!trimmed || createCommentMutation.isLoading) return;
    createCommentMutation.mutate(trimmed);
  };

  const renderCommentsContent = () => {
    if (isLoading) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(general_messages.loading)}
        </Typography>
      );
    }
    if (comments.length === 0) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(action_library_messages.instance_actions.instance_comments_empty)}
        </Typography>
      );
    }
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {comments.map((c: InstanceComment) => (
          <li
            key={c.id}
            style={{
              borderBottom: '1px solid #eee',
              padding: '8px 0',
            }}
          >
            <Typography variant='body2'>{c.text}</Typography>
            <Typography color='textSecondary' variant='caption'>
              {c.userFullName || c.userEmail} · {new Date(c.createdAt).toLocaleString()}
            </Typography>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Dialog fullWidth maxWidth='sm' onClose={onClose} open={open}>
      <DialogTitle>{t(action_library_messages.instance_actions.instance_comments_dialog_title)}</DialogTitle>
      <DialogContent dividers>
        <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 16 }}>{renderCommentsContent()}</div>
        <form onSubmit={handleSubmit}>
          <TextField
            disabled={createCommentMutation.isLoading}
            fullWidth
            label={t(action_library_messages.instance_actions.instance_comments_add_placeholder)}
            multiline
            onChange={e => setNewCommentText(e.target.value)}
            placeholder={t(action_library_messages.instance_actions.instance_comments_add_placeholder)}
            rows={2}
            value={newCommentText}
            variant='outlined'
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(general_messages.close)}</Button>
        <Button
          color='primary'
          disabled={!newCommentText.trim() || createCommentMutation.isLoading}
          onClick={handleSubmit}
          variant='contained'
        >
          {t(general_messages.add)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstanceCommentsDialog;
